/**
 * Public CSS variable names for the Char widget.
 *
 * These are the external API — host pages set these to customise the widget.
 * We maintain static lists because `getComputedStyle()` cannot enumerate
 * custom properties.
 *
 * Source of truth:
 * - `src/styles/globals.css` (`--char-*`)
 * - WebMCP ext-apps UI spec (`--color-*`, `--font-*`, etc.)
 */

const CHAR_PUBLIC_VARIABLE_NAMES = [
	// Colors - Core
	'--char-color-background',
	'--char-color-foreground',
	'--char-color-card',
	'--char-color-card-foreground',
	'--char-color-popover',
	'--char-color-popover-foreground',
	'--char-color-primary',
	'--char-color-primary-foreground',
	'--char-color-secondary',
	'--char-color-secondary-foreground',
	'--char-color-muted',
	'--char-color-muted-foreground',
	'--char-color-accent',
	'--char-color-accent-foreground',
	'--char-color-destructive',
	'--char-color-destructive-foreground',
	'--char-color-border',

	// Colors - Semantic
	'--char-color-success',
	'--char-color-warning',
	'--char-color-error',

	// Colors - Derived
	'--char-color-input',
	'--char-color-ring',

	// Message Bubbles
	'--char-user-bubble-bg',
	'--char-user-bubble-text',
	'--char-assistant-bubble-bg',
	'--char-assistant-bubble-text',

	// Composer
	'--char-composer-bg',
	'--char-composer-border',
	'--char-composer-text',
	'--char-composer-placeholder',
	'--char-composer-button-bg',
	'--char-composer-button-text',

	// Tool Cards
	'--char-tool-bg',
	'--char-tool-border',
	'--char-tool-text',
	'--char-tool-header-bg',
	'--char-tool-approve-bg',
	'--char-tool-approve-text',
	'--char-tool-deny-bg',
	'--char-tool-deny-text',

	// Code Blocks
	'--char-code-bg',
	'--char-code-text',
	'--char-code-header-bg',

	// Sizing
	'--char-radius',
	'--char-radius-sm',
	'--char-radius-md',
	'--char-radius-lg',
	'--char-radius-xl',
	'--char-radius-2xl',
	'--char-radius-3xl',
	'--char-radius-full',
	'--char-spacing-unit',

	// Typography
	'--char-font-sans',
	'--char-font-mono',
	'--char-font-size-xs',
	'--char-font-size-sm',
	'--char-font-size-base',
	'--char-font-size-lg',

	// Motion
	'--char-duration-fast',
	'--char-duration-normal',
	'--char-duration-slow',
	'--char-easing-default',
	'--char-easing-spring',

	// Z-index
	'--char-z-base',
	'--char-z-content',
	'--char-z-overlay',
	'--char-z-max',

	// Shadows
	'--char-shadow-sm',
	'--char-shadow-md',
	'--char-shadow-lg',

	// Blur
	'--char-blur-sm',
	'--char-blur-md',
	'--char-blur-lg',
] as const

const MCP_UI_STYLE_VARIABLE_NAMES = [
	// Background colors
	'--color-background-primary',
	'--color-background-secondary',
	'--color-background-tertiary',
	'--color-background-inverse',
	'--color-background-ghost',
	'--color-background-info',
	'--color-background-danger',
	'--color-background-success',
	'--color-background-warning',
	'--color-background-disabled',

	// Text colors
	'--color-text-primary',
	'--color-text-secondary',
	'--color-text-tertiary',
	'--color-text-inverse',
	'--color-text-ghost',
	'--color-text-info',
	'--color-text-danger',
	'--color-text-success',
	'--color-text-warning',
	'--color-text-disabled',

	// Border colors
	'--color-border-primary',
	'--color-border-secondary',
	'--color-border-tertiary',
	'--color-border-inverse',
	'--color-border-ghost',
	'--color-border-info',
	'--color-border-danger',
	'--color-border-success',
	'--color-border-warning',
	'--color-border-disabled',

	// Ring colors
	'--color-ring-primary',
	'--color-ring-secondary',
	'--color-ring-inverse',
	'--color-ring-info',
	'--color-ring-danger',
	'--color-ring-success',
	'--color-ring-warning',

	// Typography - Family
	'--font-sans',
	'--font-mono',

	// Typography - Weight
	'--font-weight-normal',
	'--font-weight-medium',
	'--font-weight-semibold',
	'--font-weight-bold',

	// Typography - Text Size
	'--font-text-xs-size',
	'--font-text-sm-size',
	'--font-text-md-size',
	'--font-text-lg-size',

	// Typography - Heading Size
	'--font-heading-xs-size',
	'--font-heading-sm-size',
	'--font-heading-md-size',
	'--font-heading-lg-size',
	'--font-heading-xl-size',
	'--font-heading-2xl-size',
	'--font-heading-3xl-size',

	// Typography - Text Line Height
	'--font-text-xs-line-height',
	'--font-text-sm-line-height',
	'--font-text-md-line-height',
	'--font-text-lg-line-height',

	// Typography - Heading Line Height
	'--font-heading-xs-line-height',
	'--font-heading-sm-line-height',
	'--font-heading-md-line-height',
	'--font-heading-lg-line-height',
	'--font-heading-xl-line-height',
	'--font-heading-2xl-line-height',
	'--font-heading-3xl-line-height',

	// Border radius
	'--border-radius-xs',
	'--border-radius-sm',
	'--border-radius-md',
	'--border-radius-lg',
	'--border-radius-xl',
	'--border-radius-full',

	// Border width
	'--border-width-regular',

	// Shadows
	'--shadow-hairline',
	'--shadow-sm',
	'--shadow-md',
	'--shadow-lg',
] as const

export const CHAR_CSS_VARIABLE_NAMES = [
	...CHAR_PUBLIC_VARIABLE_NAMES,
	...MCP_UI_STYLE_VARIABLE_NAMES,
] as const
